import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  submitAddMembership,
  submitCreateGroup,
  submitCreateTag,
  submitDeleteGroup,
  submitDeleteTag,
  submitRemoveMembership,
  submitReorderGroups,
  submitReorderTags,
  submitUpdateGroup,
  submitUpdateTag,
} from './venue-tag.api'
import { venueTagQueries } from './venue-tag.queries'
import type { z } from 'zod'
import type { ListSpacesParams } from '~/shared/space/space-list.params'
import type {
  AddMembershipInput,
  CreateGroupInput,
  CreateTagInput,
  DeleteGroupInput,
  DeleteTagInput,
  ListTaggedVenuesInput,
  RemoveMembershipInput,
  ReorderGroupsInput,
  ReorderTagsInput,
  UpdateGroupInput,
  UpdateTagInput,
} from '~/shared/venue-tag/venue-tag.api.schema'

const ROOT_QUERY_KEY = ['venue-tag'] as const

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ROOT_QUERY_KEY })

export const useVenueTagGroups = (options?: { enabled?: boolean }) =>
  useQuery({ ...venueTagQueries.list(), enabled: options?.enabled ?? true })

export const useTaggedVenues = (input: z.input<typeof ListTaggedVenuesInput>) =>
  useQuery(venueTagQueries.taggedVenues(input))

export const useVenueSearch = (
  query: string,
  excludeVenueIds: ReadonlyArray<number>,
  venueGroupIds: ReadonlyArray<number>,
) => useQuery(venueTagQueries.venueSearch(query, excludeVenueIds, venueGroupIds))

export const useSpaceCountsByVenue = (
  venueIds: ReadonlyArray<number>,
  activeVenueGroupId: number | null | undefined,
) => useQuery(venueTagQueries.spaceCountsByVenue(venueIds, activeVenueGroupId))

export const useVenueTagFacetCountsByVenue = (
  venueIds: ReadonlyArray<number>,
  params: ListSpacesParams,
  options?: { enabled?: boolean },
) => useQuery({ ...venueTagQueries.tagFacetCountsByVenue(venueIds, params), enabled: (options?.enabled ?? true) && venueIds.length > 0 })

// Tag mutations
export const useCreateTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof CreateTagInput>) => submitCreateTag(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useUpdateTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof UpdateTagInput>) => submitUpdateTag(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useDeleteTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof DeleteTagInput>) => submitDeleteTag(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useReorderTags = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof ReorderTagsInput>) => submitReorderTags(input),
    onSuccess: () => invalidateAll(qc),
  })
}

// Group mutations
export const useCreateGroup = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof CreateGroupInput>) => submitCreateGroup(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useUpdateGroup = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof UpdateGroupInput>) => submitUpdateGroup(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useDeleteGroup = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof DeleteGroupInput>) => submitDeleteGroup(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useReorderGroups = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof ReorderGroupsInput>) => submitReorderGroups(input),
    onSuccess: () => invalidateAll(qc),
  })
}

// Membership mutations
export const useAddMembership = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof AddMembershipInput>) => submitAddMembership(input),
    onSuccess: () => invalidateAll(qc),
  })
}

export const useRemoveMembership = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: z.infer<typeof RemoveMembershipInput>) => submitRemoveMembership(input),
    onSuccess: () => invalidateAll(qc),
  })
}
